import React from 'react';
import { FormSpy, AnyObject } from 'react-final-form';
import { FormApi } from 'final-form';
import clsx from 'clsx';
import { Grid, Typography, AccordionSummary, AccordionDetails } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { addEllipsis } from '../../../helpers/commonHelper';
import { SpeechTextField } from '../../../components/SpeechTextField';
import NotesIcon from '@material-ui/icons/Notes';
import type { Enquiry, EnquiryFormClasses } from '../EnquiryFormDrawer';
import { arePropsEqualIgnoringForm } from './arePropsEqualIgnoringForm';
// DEBUG: Field / Accordion are the original components unless the render-count flag is on (see helpers/renderCountDebug)
import { Field, createRenderCountAccordion } from '../../../helpers/renderCountDebug';

const Accordion = createRenderCountAccordion('DetailedEnquiryNotesPanel');

// Explicit request (EnquiryFormDrawer split): the "Detailed Enquiry Notes" accordion.
// Moved out of EnquiryFormDrawer.tsx with its JSX and every field's name / validate / subscription /
// handler logic unchanged. The outer component subscribes to the form (FormSpy) and passes the memoized
// body only the values it displays; event handlers read the form's values at the moment the event fires
// - exactly what the render-time `values` closure held before.
export interface DetailedEnquiryNotesPanelProps {
    form: FormApi<Enquiry>;
    classes: EnquiryFormClasses;
    isExpanded: boolean;
    onExpandChange: (event: React.ChangeEvent<{}>, expanded: boolean) => void;
}

interface DetailedEnquiryNotesPanelBodyProps extends DetailedEnquiryNotesPanelProps {
    formValues: AnyObject;
}

const DetailedEnquiryNotesPanelBody: React.FC<DetailedEnquiryNotesPanelBodyProps> = React.memo((bodyProps) => {
    const {
        classes, isExpanded, onExpandChange,
        formValues,
    } = bodyProps;
    const values = formValues;

    return (
        <Accordion
            className={classes.expansionPanel}
            expanded={isExpanded}
            onChange={onExpandChange}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="detailed-enquiry-notes-content"
                id="detailed-enquiry-notes-header"
                className={classes.expansionPanelSummary}
            >
                <div className={classes.sectionHeaderRow}>
                    <span className={clsx(classes.sectionIconChip, classes.sectionIconChipIndigo)}>
                        <NotesIcon fontSize="small" />
                    </span>
                    <Typography className={clsx(classes.heading, classes.sectionLabelIndigo, classes.sectionLabelText)}>
                        Detailed Enquiry Notes
                    </Typography>
                </div>
                <div className={classes.infoContainer}>
                    <div className="col">
                        {addEllipsis(values.responseText ?? '', 70)}
                    </div>
                </div>
            </AccordionSummary>
            <AccordionDetails
                className={classes.expandablePanelDetails}
            >
                <Grid container={true} spacing={2}>
                    <Grid item={true} xs={12} md={12} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="responseText"
                            // validate={required}
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <SpeechTextField
                                    input={input}
                                    label={('Notes')}
                                    multiline
                                    variant="outlined"
                                    size="small"
                                    className={classes.textField}
                                    autoComplete="abcd"
                                    error={meta.error && meta.touched}
                                    helperText={meta.error && meta.touched 
                                        ? 'Notes is required' 
                                        : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}, arePropsEqualIgnoringForm);
DetailedEnquiryNotesPanelBody.displayName = 'DetailedEnquiryNotesPanelBody';

export const DetailedEnquiryNotesPanel: React.FC<DetailedEnquiryNotesPanelProps> = React.memo((panelProps) => (
    <FormSpy subscription={{ values: true }}>
        {({ values }) => (
            <DetailedEnquiryNotesPanelBody
                {...panelProps}
                formValues={{
                    responseText: values.responseText,
                }}
            />
        )}
    </FormSpy>
), arePropsEqualIgnoringForm);
DetailedEnquiryNotesPanel.displayName = 'DetailedEnquiryNotesPanel';
